import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useCallback, useEffect, useState } from 'react';

export const SCROLL_OFFSET = 151;

type SidebarProps = {
  items: Field[];
  firstId: string;
  scrolling?: boolean;
  dropdownRef: React.MutableRefObject<HTMLElement>;
  wrapperRef: React.MutableRefObject<HTMLElement>;
};

const useSidebar = ({ dropdownRef, wrapperRef, firstId, items }: SidebarProps) => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [activeSubtitle, setActiveSubtitle] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const scrollTo = (id: string, subtitle: string) => {
    setShowScroll(true);
    const element = document.getElementById(id);
    setScrolling(subtitle !== activeSubtitle);
    setTimeout(() => {
      setScrolling(false);
    }, 700);
    setTimeout(() => {
      setActiveSubtitle(subtitle);
    }, 300);
    if (element) window.scrollTo({ top: element.offsetTop - SCROLL_OFFSET, behavior: 'smooth' });
  };

  const getTopCoordinateOfTitle = (id: string) => {
    return document.getElementById(id)?.getBoundingClientRect().top;
  };

  const setSidebarWidthValue = (ref: React.MutableRefObject<HTMLElement>) => {
    const dropDownTopPosition = ref.current?.getBoundingClientRect().bottom;
    if (window.innerWidth <= 1199 && !showDropdown && dropDownTopPosition <= 97) {
      setShowDropdown(false);
    } else if (!showDropdown && window.innerWidth > 1199) {
      setShowDropdown(false);
    }
    if (ref && sidebarWidth !== ref.current.offsetWidth - 30) {
      setSidebarWidth(ref.current.offsetWidth - 30);
    }
  };

  const setDropdownHeightValue = () => {
    if (dropdownHeight !== dropdownRef.current?.getBoundingClientRect().height) {
      setDropdownHeight(dropdownRef.current?.getBoundingClientRect().height);
    }
  };

  const observeHeight = (ref: React.MutableRefObject<HTMLDivElement>) => {
    const resizeObserver = new ResizeObserver(setDropdownHeightValue);
    resizeObserver.observe(ref.current);
  };

  const clickOutsideListener = useCallback(
    (e: MouseEvent) => {
      //To close dropdown if click outside of the div
      if (!(e.target as HTMLDivElement)?.closest('.dropdown-button')) {
        dropdownRef.current?.click();
      }
    },
    [dropdownRef]
  );

  const trackScreenView = useCallback(() => {
    let activeSubtitle = null;
    items.reduce((prevTitle: number, nextTitle: any) => {
      let id = nextTitle.id;
      if (wrapperRef.current.classList.contains('chapters-wrapper')) {
        id = nextTitle.fields.anchor.value;
      }
      const topCoordinate = getTopCoordinateOfTitle(id);
      if (prevTitle < 200 && !!topCoordinate && topCoordinate < 200) {
        activeSubtitle = id;
        return topCoordinate;
      } else {
        return topCoordinate;
      }
    }, getTopCoordinateOfTitle(firstId));
    setActiveSubtitle(activeSubtitle ? activeSubtitle : firstId);
    const dropDownTopPosition = wrapperRef.current.getBoundingClientRect().bottom;

    if (dropDownTopPosition <= 97 && !showDropdown && window.innerWidth <= 1199) {
      setShowDropdown(true);
    } else if (showDropdown && dropDownTopPosition > 97) {
      setShowDropdown(false);
    }
  }, [firstId, showDropdown, wrapperRef, items]);

  useEffect(() => {
    document.addEventListener('click', clickOutsideListener);
    window.addEventListener('scroll', trackScreenView);

    return () => {
      document.removeEventListener('click', clickOutsideListener);
      window.removeEventListener('scroll', trackScreenView);
    };
  }, [clickOutsideListener, trackScreenView]);

  return {
    scrollTo,
    getTopCoordinateOfTitle,
    setSidebarWidthValue,
    setDropdownHeightValue,
    observeHeight,
    clickOutsideListener,
    trackScreenView,
    showScroll,
    setShowScroll,
    scrolling,
    activeSubtitle,
    setActiveSubtitle,
    showDropdown,
    sidebarWidth,
    dropdownHeight,
  };
};
export default useSidebar;
